import React from 'react';

interface IUseSticky {
    stickyRef: React.MutableRefObject<HTMLDivElement | null>;
    sticky: boolean;
}

/**
 * Returns a ref, and a stateful value bound to the ref
 *
 * @param staticRef is the reference of the element's stickiness
 * _Based on that element, it will detect if the element has above or below the `staticRef`_
 */
export function useSticky(observerRef: React.RefObject<HTMLElement | null>): IUseSticky {
    const stickyRef = React.useRef<HTMLDivElement>(null);
    const [sticky, setSticky] = React.useState(false);

    React.useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    const ratio = entry.intersectionRatio;
                    const boundingRect = entry.boundingClientRect;
                    const intersectionRect = entry.intersectionRect;
                    const isSticky = ratio < 1 && boundingRect.top < intersectionRect.top;
                    const isNotVisibleStickyElement = boundingRect.height === 0 && intersectionRect.height === 0;

                    if (ratio === 0 && isNotVisibleStickyElement) {
                        setSticky(true);
                    }

                    if (ratio === 0 && !isSticky) {
                        return;
                    }

                    setSticky(isSticky);
                });
            },
            {
                threshold: 1.0,
            },
        );
        if (observerRef.current) {
            observer.observe(observerRef.current);
        }
    }, [observerRef]);

    return { stickyRef, sticky };
}
